import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import {
  invalidateScheduledMaintenanceCache,
  usePerformScheduledMaintenances,
} from 'src/api/scheduled-maintenance'
import { IMaintenanceListItem } from 'src/generated-types/scheduled-maintenance'
import { ShowDialogProps } from 'src/show-dialog'
import { PerformMaintenanceForm } from './PerformMaintenanceForm'

interface MaintenanceListPerformDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  item: IMaintenanceListItem
}

export const MaintenanceListPerformDialog: React.FC<MaintenanceListPerformDialogProps> = ({
  pop,
  customerId,
  localityId,
  item,
}) => {
  const { t } = useTranslation(['common-api-message', 'maintenance'])
  const mutation = usePerformScheduledMaintenances(customerId, localityId)

  const onSubmit = useCallback(
    async form => {
      await mutation.mutateAsync(
        {
          ...form,
          scheduledMaintenanceIds: [item.id],
          isSinglePerform: true,
        },
        {
          onSuccess: () => {
            invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId)
            invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(customerId, localityId)
            invalidateScheduledMaintenanceCache.getMaintenanceList(customerId, localityId)
            invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
            invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId)
            invalidateScheduledMaintenanceCache.getEquipmentPerformedMaintenances(customerId, localityId)
            invalidateScheduledMaintenanceCache.getLocalityPerformedMaintenances(customerId, localityId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidationActivityLogCache.getCageMaintenanceLogs()
            toast.success(t('performed-successfully', { ns: 'maintenance' }))
            pop()
          },
          onError: () => {
            toast.error(t('unExpectedError', { ns: 'common-api-message' }))
          },
        },
      )
    },
    [mutation, item.id, customerId, localityId, t, pop],
  )

  const initialPerformedDate =
    item.isOverdue && item.scheduledDate ? item.scheduledDate.split('T')[0] : undefined

  return (
    <PerformMaintenanceForm
      onSubmit={onSubmit}
      onCancel={pop}
      maintenanceName={item.maintenanceName}
      note={item.maintenanceNote}
      isPerformAll={false}
      initialPerformedDate={initialPerformedDate}
      isSubmitting={mutation.isLoading}
    />
  )
}
